import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, Clock, Sun, Sunset, Moon, Star, Coffee, Utensils,
  FlaskConical, Pill, Activity, ChevronDown, SkipForward, Edit3,
  TrendingUp, TrendingDown, Minus, AlertCircle, Target, Zap
} from 'lucide-react';
import DailyReflectionPrompt from '@/components/DailyReflectionPrompt';
import SubscriberNeuroFlowTab from '@/pages/subscriber/SubscriberNeuroFlowTab';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL INTERFACE — DAILY FLOW EXPERIENCE
// ══════════════════════════════════════════════════════════════════════════════

// ── Trigger mapping: context → knowledge trigger type ────────────────────────
// Maps friction points to the most relevant biological explanation
const TRIGGER_MAP = {
  medication_delay: 'timing_conflict',
  medication_skip: 'off_worsening',
  food_delay: 'timing_conflict',
  food_skip: 'gut_instability',
  generic_delay: 'adherence_drop',
  generic_skip: 'adherence_drop',
  hint_risk_alert: 'adherence_drop',
  hint_delay_warning: 'timing_conflict',
};

// Block metadata (icons + soft colors + perception-first subtitle)
const BLOCK_META = {
  wake:        { icon: Sun,     label: 'Wake',        subtitle: 'Wake Rhythm',           phase: 'morning' },
  morning:     { icon: Sun,     label: 'Morning',     subtitle: 'Morning Activation',    phase: 'morning' },
  mid_morning: { icon: Coffee,  label: 'Mid-Morning', subtitle: 'Mid-Morning Support',   phase: 'morning' },
  lunch:       { icon: Utensils,label: 'Midday',      subtitle: 'Lunch Stability',       phase: 'midday' },
  afternoon:   { icon: Sunset,  label: 'Afternoon',   subtitle: 'Afternoon Regulation',  phase: 'afternoon' },
  evening:     { icon: Sunset,  label: 'Evening',     subtitle: 'Evening Alignment',     phase: 'evening' },
  dinner:      { icon: Utensils,label: 'Dinner',      subtitle: 'Dinner Response',       phase: 'evening' },
  night:       { icon: Moon,    label: 'Night',       subtitle: 'Night Recovery',        phase: 'night' },
  sleep:       { icon: Star,    label: 'Sleep',       subtitle: 'Sleep Preparation',     phase: 'night' },
  anytime:     { icon: Clock,   label: 'Flexible',    subtitle: 'Flexible Window',       phase: 'flexible' },
};

// Type styling (minimal, soft)
const TYPE_STYLE = {
  regenflo:   { icon: FlaskConical, color: '#4A5D4E' },
  medication: { icon: Pill,         color: '#5C6B8A' },
  food:       { icon: Utensils,     color: '#8C6239' },
  lifeflo:    { icon: Activity,     color: '#7A5D8A' },
};

// State themes (emotional system)
const STATE_THEMES = {
  stable:      { bg: '#E8ECE6', text: '#4A5D4E', accent: '#738C78' },
  improving:   { bg: '#E1EDE4', text: '#2E6B3B', accent: '#4D9A5E' },
  fluctuating: { bg: '#F5ECE1', text: '#8C6239', accent: '#C4925D' },
  declining:   { bg: '#F2E6E6', text: '#8A4343', accent: '#BD6B6B' },
};

// Hint styling (micro coaching)
const HINT_STYLE = {
  risk_alert:              { icon: AlertCircle,  color: '#8A4343', bg: '#F2E6E6' },
  delay_warning:           { icon: Clock,        color: '#8C6239', bg: '#F5ECE1' },
  improvement_signal:      { icon: TrendingUp,   color: '#2E6B3B', bg: '#E1EDE4' },
  stability_reinforcement: { icon: Target,       color: '#4A5D4E', bg: '#E8ECE6' },
};

// Helper: format time
function fmt24to12(t24) {
  if (!t24) return '';
  const [h, m] = t24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Helper: get current time block
function getCurrentBlock(blocks) {
  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    if (block.groups && block.groups.length > 0) {
      const firstTime = block.groups[0]?.planned_time;
      if (firstTime && currentTime >= firstTime) {
        return block.block_key;
      }
    }
  }
  return blocks[0]?.block_key || null;
}

// ══════════════════════════════════════════════════════════════════════════════
// INLINE UNDERSTAND — Soft expansion at point of friction
// ══════════════════════════════════════════════════════════════════════════════
// Does NOT navigate to /knowledge. Unfolds like a thought within the flow.
// Max 4-6 lines per section. Closes the loop: understand → action.

function InlineUnderstand({ triggerType, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!triggerType) return;
    axios.get(`${API}/knowledge/explain/${triggerType}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [triggerType]);

  if (loading) return (
    <div className="py-3 pl-4 border-l-2 border-[#C89B7B]/20">
      <div className="w-4 h-4 border border-[#C89B7B]/30 border-t-[#C89B7B] rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;

  const sections = [
    { label: 'What is happening', text: data.what_is_happening },
    { label: 'Why', text: data.why_it_happens },
    { label: 'What it affects', text: data.what_it_affects },
    { label: 'What helps', text: data.what_helps },
  ];

  return (
    <div className="mt-3 mb-2 pl-4 border-l-2 border-[#C89B7B]/20 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="inline-understand">
      {sections.map((s, i) => (
        <div key={i}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#8A8681] mb-0.5 font-['Work_Sans']">{s.label}</p>
          <p className="text-xs text-[#5C5955] leading-[1.75] font-['Work_Sans'] font-light">{s.text}</p>
        </div>
      ))}
      <button onClick={onClose} className="text-[10px] text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] transition-colors" data-testid="close-inline-understand">
        Close
      </button>
    </div>
  );
}

// Helper: determine relevant trigger from context
function getRelevantTrigger(eventType, action) {
  const key = eventType === 'medication'
    ? `medication_${action}`
    : eventType === 'food'
    ? `food_${action}`
    : `generic_${action}`;
  return TRIGGER_MAP[key] || 'adherence_drop';
}

// ══════════════════════════════════════════════════════════════════════════════
// EXCEPTION DIALOG (Simplified)
// ══════════════════════════════════════════════════════════════════════════════

function ExceptionDialog({ open, onClose, item, onSubmit, mode }) {
  const [formData, setFormData] = useState({
    status: mode || 'changed',
    actual_time: item?.planned_time || '',
    actual_detail_dose: item?.planned_detail_dose || '',
    reason: '',
    notes: '',
  });

  useEffect(() => {
    if (item) {
      setFormData({
        status: mode || 'changed',
        actual_time: item.planned_time || '',
        actual_detail_dose: item.planned_detail_dose || '',
        reason: '',
        notes: '',
      });
    }
  }, [item, mode]);

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#FBFaf9] border-[#EBE8E3]">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-[#2C2A29] font-normal">
            {mode === 'skipped' ? 'Skip Item' : 'Log Change'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-[#5C5955] font-['Work_Sans']">{item.name}</p>
          
          {mode === 'changed' && (
            <>
              <div className="space-y-2">
                <Label className="text-[#5C5955] text-xs uppercase tracking-wide font-['Work_Sans']">Actual Time</Label>
                <Input
                  type="time"
                  value={formData.actual_time}
                  onChange={e => setFormData({ ...formData, actual_time: e.target.value })}
                  className="bg-[#F4F2EE] border-[#EBE8E3] text-[#2C2A29] font-['Work_Sans']"
                />
              </div>
              {item.planned_detail_dose && (
                <div className="space-y-2">
                  <Label className="text-[#5C5955] text-xs uppercase tracking-wide font-['Work_Sans']">Actual Dose</Label>
                  <Input
                    value={formData.actual_detail_dose}
                    onChange={e => setFormData({ ...formData, actual_detail_dose: e.target.value })}
                    className="bg-[#F4F2EE] border-[#EBE8E3] text-[#2C2A29] font-['Work_Sans']"
                  />
                </div>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label className="text-[#5C5955] text-xs uppercase tracking-wide font-['Work_Sans']">Reason</Label>
            <Select value={formData.reason} onValueChange={v => setFormData({ ...formData, reason: v })}>
              <SelectTrigger className="bg-[#F4F2EE] border-[#EBE8E3] text-[#2C2A29] font-['Work_Sans']">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent className="bg-[#FBFaf9] border-[#EBE8E3]">
                <SelectItem value="forgot">Forgot</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="not_feeling_well">Not feeling well</SelectItem>
                <SelectItem value="side_effects">Side effects</SelectItem>
                <SelectItem value="ran_out">Ran out</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#5C5955] text-xs uppercase tracking-wide font-['Work_Sans']">Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              className="bg-[#F4F2EE] border-[#EBE8E3] text-[#2C2A29] font-['Work_Sans'] resize-none"
              rows={2}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-[#5C5955] hover:text-[#2C2A29] hover:bg-[#EBE8E3] font-['Work_Sans']">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-[#4A5D4E] hover:bg-[#3A4D3E] text-white font-['Work_Sans']"
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAKE NOW DIALOG — Adaptive medication execution
// ══════════════════════════════════════════════════════════════════════════════

const REASON_TAGS = [
  { key: 'off_starting', label: 'OFF starting' },
  { key: 'stiffness_rising', label: 'Stiffness rising' },
  { key: 'functional_demand', label: 'Functional demand' },
  { key: 'preventive_before_meeting', label: 'Before meeting' },
  { key: 'digestion_delay', label: 'Digestion delay' },
  { key: 'dyskinesia_settling', label: 'Dyskinesia settling' },
];

function TakeNowDialog({ open, onClose, onSubmit, loading: submitting }) {
  const [reasonTag, setReasonTag] = useState(null);

  const handleSubmit = () => {
    onSubmit(reasonTag);
    setReasonTag(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm bg-[#FBFaf9] border-[#EBE8E3]" data-testid="take-now-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-[#2C2A29] font-normal flex items-center gap-2">
            <Pill className="w-4 h-4 text-[#5C6B8A]" strokeWidth={1.5} /> Take Now
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-[#8A8681] font-['Work_Sans']">
            Optional — why are you taking now?
          </p>
          <div className="flex flex-wrap gap-2">
            {REASON_TAGS.map(tag => (
              <button
                key={tag.key}
                onClick={() => setReasonTag(reasonTag === tag.key ? null : tag.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-['Work_Sans'] transition-colors ${
                  reasonTag === tag.key
                    ? 'bg-[#5C6B8A] text-white'
                    : 'bg-[#F4F2EE] text-[#5C5955] hover:bg-[#EBE8E3]'
                }`}
                data-testid={`reason-tag-${tag.key}`}
              >
                {tag.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className="text-[#5C5955] hover:text-[#2C2A29] font-['Work_Sans'] text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#5C6B8A] hover:bg-[#4C5B7A] text-white font-['Work_Sans'] text-sm rounded-full px-6"
              data-testid="confirm-take-now"
            >
              {submitting ? 'Logging...' : 'Log Dose'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// GUARDRAIL ALERT — Soft post-action feedback
// ══════════════════════════════════════════════════════════════════════════════

function GuardrailAlert({ alerts, onDismiss }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="space-y-2 mt-2 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="guardrail-alerts">
      {alerts.map((alert, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-['Work_Sans'] ${
            alert.severity === 'attention' ? 'bg-[#F2E6E6] text-[#8A4343]' :
            alert.severity === 'watch' ? 'bg-[#F5ECE1] text-[#8C6239]' :
            'bg-[#E8ECE6] text-[#4A5D4E]'
          }`}
        >
          <AlertCircle className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
          <span className="flex-1">{alert.message}</span>
        </div>
      ))}
      <button onClick={onDismiss} className="text-[10px] text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] ml-3">
        Dismiss
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FLOW ITEM (Minimal, No Heavy Borders)
// ══════════════════════════════════════════════════════════════════════════════

function FlowItem({ item, overlay, onConfirm, onException, onTakeNow, confirming }) {
  const ts = TYPE_STYLE[item.event_type] || TYPE_STYLE.lifeflo;
  const TypeIcon = ts.icon;
  const isCompleted = item.status === 'as_scheduled';
  const isChanged = item.status === 'changed';
  const isSkipped = item.status === 'skipped';
  const isPending = item.status === 'pending';
  const isMedication = item.event_type === 'medication';
  const [showUnderstand, setShowUnderstand] = useState(false);

  // Phase E — overlay-driven flags. Overlay is null when NeuroFlow is not
  // active or has no matching item. Overlay never mutates canonical data.
  const overlayStatus = overlay?.status;
  const isNFHeld = overlayStatus === 'held';
  const isNFSkipped = overlayStatus === 'skipped';
  const isNFMoved = overlayStatus === 'moved';
  const isNFAnchor = overlay?.item_type === 'medicine_anchor';
  const nfBlocksAction = (isNFHeld || isNFSkipped) && isPending;

  // Show "Understand this" only for skipped or delayed (changed) items — points of friction
  const hasFriction = isSkipped || isChanged;
  const showUnderstandOption = hasFriction && !showUnderstand;
  const triggerType = hasFriction ? getRelevantTrigger(item.event_type, isSkipped ? 'skip' : 'delay') : null;

  return (
    <div data-testid={`flow-item-${item.code}`} data-nf-status={overlayStatus || 'none'}>
      <div 
        className={`flex flex-wrap items-center gap-x-4 gap-y-1 py-3 transition-all duration-500 ${
          isCompleted || isChanged ? 'opacity-50' : isSkipped ? 'opacity-30' : ''
        } ${nfBlocksAction ? 'opacity-60' : ''}`}
      >
      {/* Time */}
      <span className="text-sm font-['Work_Sans'] text-[#8A8681] w-16 flex-shrink-0">
        {fmt24to12(item.planned_time)}
      </span>

      {/* Icon + Name */}
      <div className="flex items-center gap-2 flex-1 min-w-[100px]">
        <TypeIcon className="w-4 h-4 flex-shrink-0" style={{ color: ts.color }} strokeWidth={1.25} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            <span className={`text-base font-['Work_Sans'] ${isSkipped ? 'line-through' : ''}`} style={{ color: '#2C2A29' }}>
              {item.name}
            </span>
            {item.planned_detail_dose && (
              <span className="text-sm text-[#8A8681] font-['Work_Sans'] flex-shrink-0">
                {item.planned_detail_dose}
              </span>
            )}
          </div>
          {/* Why this matters — perception-first one-liner derived from item type */}
          {(() => {
            const why = (item.why_this_matters)
              || (item.kind === 'medication' && 'Helps maintain daily input consistency.')
              || (item.kind === 'food' && 'Supports gut calmness and terrain stability.')
              || (item.kind === 'supplement' && 'Daily input that supports your alignment pathway.')
              || (item.kind === 'movement' && 'Supports circulation and rhythm.')
              || (item.kind === 'rest' && 'Supports recovery and night rhythm.')
              || (item.kind === 'mind' && 'Supports nervous system rhythm.');
            return why && isPending ? (
              <p className="text-[10.5px] text-[#8C6B55] font-['Work_Sans'] leading-snug mt-0.5 italic">
                {why}
              </p>
            ) : null;
          })()}
          {/* Phase E — NeuroFlow overlay badges & reasons */}
          {overlay && overlayStatus && overlayStatus !== 'planned' && overlayStatus !== 'completed' && (
            <div data-testid={`nf-overlay-${item.code}`} className="mt-1 flex flex-wrap items-baseline gap-x-2">
              {isNFHeld && (
                <span data-testid={`nf-overlay-${item.code}-held`} className="text-[10px] font-['Work_Sans'] inline-block px-1.5 py-0.5 rounded-full bg-[#F5ECE1] text-[#8C6239] border border-[#E5D6BE]">
                  Held by care plan today
                </span>
              )}
              {isNFSkipped && (
                <span data-testid={`nf-overlay-${item.code}-skipped`} className="text-[10px] font-['Work_Sans'] inline-block px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                  Skipped today
                </span>
              )}
              {isNFMoved && (
                <span data-testid={`nf-overlay-${item.code}-moved`} className="text-[10px] font-['Work_Sans'] inline-block px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                  Moved to {overlay.adjusted_time || '—'}
                </span>
              )}
              {overlay.reason && (
                <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">
                  {overlay.reason}
                </span>
              )}
              {isNFMoved && overlay.planned_time && (
                <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">
                  · originally {overlay.planned_time}
                </span>
              )}
            </div>
          )}
          {isNFAnchor && (
            <span data-testid={`nf-anchor-locked-${item.code}`} className="text-[10px] font-['Work_Sans'] inline-block mt-1 px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
              Medicine anchor — not changed by NeuroFlow.
            </span>
          )}
          {isChanged && item.actual_time && item.actual_time !== item.planned_time && (
            <span className="text-xs text-[#8C6239] font-['Work_Sans'] block mt-0.5">
              → {fmt24to12(item.actual_time)}
            </span>
          )}
          {/* Show variance badge for adaptive takes */}
          {(isCompleted || isChanged) && item.timing_variance_minutes != null && Math.abs(item.timing_variance_minutes) > 5 && (
            <span className={`text-[10px] font-['Work_Sans'] inline-block mt-0.5 px-1.5 py-0.5 rounded-full ${
              item.timing_variance_minutes < 0 ? 'bg-[#E1EDE4] text-[#2E6B3B]' : 'bg-[#F5ECE1] text-[#8C6239]'
            }`}>
              {item.timing_variance_minutes > 0 ? '+' : ''}{item.timing_variance_minutes}m
            </span>
          )}
        </div>
      </div>

      {/* Actions or Status */}
      {isPending && nfBlocksAction ? (
        <div data-testid={`nf-action-blocked-${item.code}`} className="flex items-center flex-shrink-0 ml-auto">
          <span className="text-[10px] font-['Work_Sans'] px-2 py-1 rounded-full bg-[#F5ECE1] text-[#8C6239] border border-[#E5D6BE]">
            {isNFHeld ? 'Held by care plan today' : 'Skipped today'}
          </span>
        </div>
      ) : isPending ? (
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          {/* Take Now — prominent for medication items */}
          {isMedication && onTakeNow && (
            <button
              onClick={() => onTakeNow(item)}
              disabled={confirming}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#5C6B8A]/10 hover:bg-[#5C6B8A]/20 text-[#5C6B8A] transition-colors duration-300"
              data-testid={`take-now-${item.code}`}
            >
              <Zap className="w-3 h-3" strokeWidth={2} />
              <span className="text-xs font-['Work_Sans'] font-medium">Take Now</span>
            </button>
          )}
          <button
            onClick={() => onConfirm(item.item_id)}
            disabled={confirming}
            className="p-2 rounded-full hover:bg-[#E8ECE6] transition-colors duration-300"
            data-testid={`confirm-${item.code}`}
          >
            <Check className="w-4 h-4 text-[#4A5D4E]" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onException(item, 'changed')}
            className="p-2 rounded-full hover:bg-[#F5ECE1] transition-colors duration-300"
            data-testid={`change-${item.code}`}
          >
            <Edit3 className="w-4 h-4 text-[#8C6239]" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onException(item, 'skipped')}
            className="p-2 rounded-full hover:bg-[#F2E6E6] transition-colors duration-300"
            data-testid={`skip-${item.code}`}
          >
            <SkipForward className="w-4 h-4 text-[#8A4343]" strokeWidth={1.5} />
          </button>
        </div>
      ) : (
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isSkipped ? '#F2E6E6' : '#E8ECE6' }}>
          {isSkipped ? (
            <SkipForward className="w-3 h-3 text-[#8A4343]" strokeWidth={1.5} />
          ) : (
            <Check className="w-3 h-3 text-[#4A5D4E]" strokeWidth={2} />
          )}
        </div>
      )}
      </div>

      {/* "Understand this" for skipped/delayed items — friction point */}
      {showUnderstandOption && (
        <div className="pl-20 -mt-1 mb-1">
          <button
            onClick={() => setShowUnderstand(true)}
            className="text-[10px] text-[#8C6B55]/50 hover:text-[#8C6B55] font-['Work_Sans'] transition-colors"
            data-testid={`understand-item-${item.code}`}
          >
            Understand this
          </button>
        </div>
      )}

      {/* Inline expansion — understanding unfolds at the moment */}
      {showUnderstand && triggerType && (
        <div className="pl-20 mb-2">
          <InlineUnderstand
            triggerType={triggerType}
            onClose={() => setShowUnderstand(false)}
          />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FLOW BLOCK (Soft, Flowing Section)
// ══════════════════════════════════════════════════════════════════════════════

function FlowBlock({ block, hint, isCurrent, onConfirm, onConfirmGroup, onException, onTakeNow, confirming, getOverlayFor }) {
  const meta = BLOCK_META[block.block_key] || BLOCK_META.morning;
  const BlockIcon = meta.icon;
  const hintStyle = hint ? HINT_STYLE[hint.type] : null;
  const HintIcon = hintStyle?.icon;
  const [showUnderstand, setShowUnderstand] = useState(false);

  const allItems = block.groups.flatMap(g => g.items);
  const pendingCount = allItems.filter(i => i.status === 'pending').length;
  const completedCount = allItems.filter(i => i.status !== 'pending').length;
  
  // Block state based on completion
  const blockState = pendingCount === 0 ? 'completed' : completedCount > 0 ? 'in_progress' : 'upcoming';

  return (
    <div 
      className={`relative transition-all duration-500 ${isCurrent ? 'scale-[1.01]' : ''}`}
      data-testid={`flow-block-${block.block_key}`}
    >
      {/* Current Block Highlight */}
      {isCurrent && (
        <div 
          className="absolute -inset-4 rounded-3xl -z-10 transition-opacity duration-700"
          style={{ backgroundColor: '#E8ECE6', opacity: 0.5 }}
        />
      )}

      {/* Block Header */}
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-500"
          style={{ backgroundColor: isCurrent ? '#E8ECE6' : '#F4F2EE' }}
        >
          <BlockIcon className="w-5 h-5" style={{ color: isCurrent ? '#4A5D4E' : '#8A8681' }} strokeWidth={1.25} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-['Outfit'] font-normal" style={{ color: isCurrent ? '#2C2A29' : '#5C5955' }}>
            {meta.label}
          </h3>
          {meta.subtitle && meta.subtitle !== meta.label ? (
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6B55] font-['Work_Sans']">
              {meta.subtitle}
            </p>
          ) : null}
          {isCurrent && (
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] uppercase tracking-wide">
              {blockState === 'completed' ? 'Completed' : 'Currently in this phase'}
            </p>
          )}
        </div>
        {pendingCount > 0 && (
          <button
            onClick={() => onConfirmGroup(block)}
            disabled={confirming}
            className="px-4 py-2 rounded-full text-sm font-['Work_Sans'] text-[#4A5D4E] hover:bg-[#E8ECE6] transition-colors duration-300"
            data-testid={`confirm-all-${block.block_key}`}
          >
            Complete all ({pendingCount})
          </button>
        )}
      </div>

      {/* State Line / Micro Coaching Hint — with "Understand why" */}
      {hint && HintIcon && (
        <div className="mb-4">
          <div 
            className="flex items-center gap-3 py-3 px-4 rounded-2xl transition-colors duration-500"
            style={{ backgroundColor: hintStyle.bg }}
            data-testid={`hint-${block.block_key}`}
          >
            <HintIcon className="w-4 h-4 flex-shrink-0" style={{ color: hintStyle.color }} strokeWidth={1.25} />
            <span className="text-sm font-['Work_Sans'] flex-1" style={{ color: hintStyle.color }}>
              {hint.hint}
            </span>
            {/* "Understand why" — only for friction hints (risk_alert, delay_warning) */}
            {(hint.type === 'risk_alert' || hint.type === 'delay_warning') && !showUnderstand && (
              <button
                onClick={() => setShowUnderstand(true)}
                className="text-[10px] text-[#8C6B55]/60 hover:text-[#8C6B55] font-['Work_Sans'] transition-colors whitespace-nowrap"
                data-testid={`understand-hint-${block.block_key}`}
              >
                Understand why
              </button>
            )}
          </div>
          {/* Inline expansion — at the moment of friction, not elsewhere */}
          {showUnderstand && (
            <InlineUnderstand
              triggerType={TRIGGER_MAP[`hint_${hint.type}`] || 'adherence_drop'}
              onClose={() => setShowUnderstand(false)}
            />
          )}
        </div>
      )}

      {/* Items (Flowing, No Heavy Borders) */}
      <div className="space-y-1 pl-14">
        {allItems.map((item, idx) => (
          <FlowItem
            key={item.item_id || idx}
            item={item}
            overlay={getOverlayFor ? getOverlayFor(item) : null}
            onConfirm={onConfirm}
            onException={onException}
            onTakeNow={onTakeNow}
            confirming={confirming}
          />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHASE E — NEUROFLOW DAILY STATUS BANNER (read-only overlay on /daily)
// ══════════════════════════════════════════════════════════════════════════════
// Pulls entirely from the overlay payload already fetched by the parent.
// Never mutates canonical data. Hidden unless module_active=true.

const NF_STATE_COPY = {
  stable_on_day:               'Your response pattern looks stable today. Continue today’s plan.',
  delayed_on:                  'Medicine response appears delayed. Please wait and log when ON starts.',
  no_on_risk:                  'Medicine effect is not appearing as expected. Please contact your care team.',
  early_off:                   'OFF started earlier than expected. Log the next medicine and symptoms carefully.',
  peak_dyskinesia:             'High movement activity noted. Avoid adding extra ReGenflō items unless your care team advises.',
  stress_off:                  'Stress or activity may be affecting your movement today. Reduce pressure if possible and keep logging.',
  meal_interference_day:       'Food may have affected medicine response. Avoid heavy/protein/fat food before the next dose.',
  gut_positive_motor_neutral:  'Gut response looks improved and movement response is stable.',
  gut_positive_motor_negative: 'Gut response improved but movement response needs review. Your care team will check this.',
  calibration_intolerance:     'Your care team needs to review today’s pattern before the next change.',
};

function nfStatusFromGate(gate) {
  if (gate === 'severe_red') return { label: 'Review needed', cls: 'bg-rose-100 text-rose-700 border-rose-300' };
  if (gate === 'red')        return { label: 'Hold',          cls: 'bg-rose-50  text-rose-700 border-rose-200' };
  if (gate === 'yellow')     return { label: 'Watch',         cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (gate === 'green')      return { label: 'Stable',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { label: 'Today’s plan', cls: 'bg-stone-100 text-stone-700 border-stone-200' };
}

function NeuroFlowDailyBanner({ overlay, overlayError }) {
  if (overlayError) {
    return (
      <div
        data-testid="nf-daily-banner-error"
        className="mb-6 rounded-2xl border border-[#EBE8E3] bg-[#F4F2EE] p-4 text-xs text-[#8A8681] font-['Work_Sans']"
      >
        NeuroFlow overlay unavailable right now. Your daily plan below is unchanged.
      </div>
    );
  }
  if (!overlay || overlay.module_active !== true) return null;

  const gate = overlay.gate_status;
  const status = nfStatusFromGate(gate);
  const items = overlay.overlays || [];
  const counts = items.reduce(
    (acc, it) => {
      if (it.status === 'held') acc.held += 1;
      else if (it.status === 'skipped') acc.skipped += 1;
      else if (it.status === 'moved') acc.moved += 1;
      return acc;
    },
    { held: 0, skipped: 0, moved: 0 },
  );
  const stateCopy =
    NF_STATE_COPY[overlay.current_state]
    || 'Continue today’s plan. Keep logging events as they happen.';
  const isUrgent = gate === 'red' || gate === 'severe_red';

  return (
    <div
      data-testid="nf-daily-banner"
      data-nf-gate={gate || 'none'}
      className="mb-8 rounded-2xl border border-[#D8D4CE] bg-[#F4F2EE] p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          data-testid="nf-daily-banner-status-pill"
          className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${status.cls}`}
        >
          {status.label}
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] font-['Work_Sans']">
          NeuroFlow · today
        </span>
      </div>
      <p
        data-testid="nf-daily-banner-state-copy"
        className="text-sm text-[#5C5955] font-['Work_Sans'] leading-relaxed"
      >
        {stateCopy}
      </p>

      {(counts.held + counts.skipped + counts.moved) > 0 && (
        <div
          data-testid="nf-daily-banner-counts"
          className="mt-3 flex flex-wrap gap-2"
        >
          {counts.held > 0 && (
            <span
              data-testid="nf-daily-banner-count-held"
              className="text-[11px] font-['Work_Sans'] px-2 py-0.5 rounded-full bg-[#F5ECE1] text-[#8C6239] border border-[#E5D6BE]"
            >
              {counts.held} held today
            </span>
          )}
          {counts.skipped > 0 && (
            <span
              data-testid="nf-daily-banner-count-skipped"
              className="text-[11px] font-['Work_Sans'] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
            >
              {counts.skipped} skipped today
            </span>
          )}
          {counts.moved > 0 && (
            <span
              data-testid="nf-daily-banner-count-moved"
              className="text-[11px] font-['Work_Sans'] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
            >
              {counts.moved} moved today
            </span>
          )}
        </div>
      )}

      {isUrgent && (
        <div
          data-testid="nf-daily-banner-safety"
          className="mt-3 rounded-xl border-2 border-rose-300 bg-rose-50 p-3 flex items-start gap-2"
        >
          <AlertCircle className="w-4 h-4 text-rose-700 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
          <span className="text-xs text-rose-900 font-['Work_Sans']">
            Please contact your care team. Do not make medicine changes on your own.
          </span>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN: DAILY FLOW EXPERIENCE
// ══════════════════════════════════════════════════════════════════════════════

export default function DailyFlowExperience() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [exceptionItem, setExceptionItem] = useState(null);
  const [exceptionMode, setExceptionMode] = useState('changed');
  const [dynamicFeedback, setDynamicFeedback] = useState(null);
  const [takeNowItem, setTakeNowItem] = useState(null);
  const [takeNowLoading, setTakeNowLoading] = useState(false);
  const [guardrailAlerts, setGuardrailAlerts] = useState([]);
  const currentBlockRef = useRef(null);

  // ── Phase D: NeuroFlow tab visibility ─────────────────────────
  const [activeTab, setActiveTab] = useState('daily');
  const [nfActive, setNfActive] = useState(false);

  // ── Phase E: NeuroFlow overlay on Daily Execution ──────────────
  const [nfOverlay, setNfOverlay] = useState(null);
  const [nfOverlayError, setNfOverlayError] = useState(false);

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;
  const today = new Date().toISOString().split('T')[0];

  // Probe whether NeuroFlow module is active for this subscriber.
  // Errors are silent — if the endpoint 403s or 404s, the tab stays hidden.
  useEffect(() => {
    if (!subscriberId || authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/neuroflow/${subscriberId}/today`);
        if (!cancelled) setNfActive(r.data?.module_active === true);
      } catch {
        if (!cancelled) setNfActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subscriberId, authLoading]);

  // Phase E: load NeuroFlow overlay when active. Failure → fallback note;
  // canonical Daily Execution still renders unchanged.
  useEffect(() => {
    if (!subscriberId || authLoading || !nfActive) {
      setNfOverlay(null);
      setNfOverlayError(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/neuroflow/${subscriberId}/overlay/today`);
        if (!cancelled) {
          setNfOverlay(r.data || null);
          setNfOverlayError(false);
        }
      } catch {
        if (!cancelled) {
          setNfOverlay(null);
          setNfOverlayError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [subscriberId, authLoading, nfActive]);

  // Build a lookup keyed by linked_protocol_item_id (priority) and
  // linked_anchor_id (fallback). Per spec §"Overlay matching": ID-first.
  const nfOverlayByItemId = useMemo(() => {
    const map = new Map();
    (nfOverlay?.overlays || []).forEach((o) => {
      if (o.linked_protocol_item_id) map.set(o.linked_protocol_item_id, o);
      if (o.linked_anchor_id) map.set(o.linked_anchor_id, o);
    });
    return map;
  }, [nfOverlay]);

  const getOverlayFor = useCallback((item) => {
    if (!nfOverlayByItemId.size || !item) return null;
    return (
      nfOverlayByItemId.get(item.item_id)
      || nfOverlayByItemId.get(item.code)
      || null
    );
  }, [nfOverlayByItemId]);

  const fetchFlow = useCallback(async () => {
    if (!subscriberId || authLoading) return;
    setLoading(true);
    const token = localStorage.getItem('rf_access_token');
    if (!token) {
      setLoading(false);
      setData(null);
      return;
    }

    try {
      const [flowResponse, hintsResponse] = await Promise.all([
        fetch(`${API}/v3/subscribers/${subscriberId}/daily-flow?date=${today}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
        }),
        fetch(`${API}/micro-coaching/${subscriberId}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
        }).catch(() => ({ ok: false }))
      ]);

      if (!flowResponse.ok) throw new Error(`HTTP ${flowResponse.status}`);

      const responseData = await flowResponse.json();
      setData(responseData);

      if (hintsResponse.ok) {
        const hintsData = await hintsResponse.json();
        setHints(hintsData.hints || {});
      }
    } catch (err) {
      console.error('[Flow] fetch error:', err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [subscriberId, today, authLoading]);

  useEffect(() => { fetchFlow(); }, [fetchFlow]);

  // Dynamic Feedback — warm, subtle response after user actions
  const requestDynamicFeedback = useCallback(async (action, blockKey, itemName) => {
    try {
      const params = new URLSearchParams({ action, block: blockKey || '', item_name: itemName || '' });
      const res = await axios.post(`${API}/dynamic-feedback/${subscriberId}?${params}`);
      if (res.data?.feedback?.message) {
        setDynamicFeedback(res.data.feedback);
        setTimeout(() => setDynamicFeedback(null), 5000);
      }
    } catch {
      // Feedback is non-critical — silent failure
    }
  }, [subscriberId]);

  // Scroll to current block on load
  useEffect(() => {
    if (data?.has_flow && currentBlockRef.current) {
      setTimeout(() => {
        currentBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [data]);

  const handleConfirm = async (itemId) => {
    setConfirming(true);
    try {
      let blockKey = '', itemName = '';
      if (data?.blocks) {
        for (const block of data.blocks) {
          const allItems = block.groups.flatMap(g => g.items);
          const found = allItems.find(i => i.item_id === itemId);
          if (found) { blockKey = block.block_key; itemName = found.name; break; }
        }
      }
      await axios.post(`${API}/v3/subscribers/${subscriberId}/daily-flow/${itemId}/confirm`, { execution_date: today });
      toast.success('Completed');
      requestDynamicFeedback('completed', blockKey, itemName);
      fetchFlow();
    } catch { toast.error('Failed'); }
    finally { setConfirming(false); }
  };

  const handleConfirmGroup = async (block) => {
    setConfirming(true);
    try {
      const allItems = block.groups.flatMap(g => g.items);
      const pendingIds = allItems.filter(i => i.status === 'pending').map(i => i.item_id);
      await axios.post(`${API}/v3/subscribers/${subscriberId}/daily-flow/confirm-group`, {
        execution_date: today, item_ids: pendingIds,
      });
      toast.success(`${pendingIds.length} items completed`);
      fetchFlow();
    } catch { toast.error('Failed'); }
    finally { setConfirming(false); }
  };

  // ── ADAPTIVE TAKE NOW ──────────────────────────────────────
  const handleOpenTakeNow = (item) => {
    setTakeNowItem(item);
  };

  const handleSubmitTakeNow = async (reasonTag) => {
    if (!takeNowItem) return;
    setTakeNowLoading(true);
    try {
      const now = new Date();
      const actualTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const res = await axios.post(`${API}/levodopa/take-now/${subscriberId}`, {
        execution_date: today,
        item_id: takeNowItem.item_id,
        actual_time: actualTime,
        reason_tag: reasonTag || null,
      });
      const result = res.data;
      const mapped = result.mapped_to;
      const variance = result.variance_minutes;
      const label = variance > 0 ? `+${variance}m late` : variance < 0 ? `${variance}m early` : 'on time';
      toast.success(`${mapped?.name || 'Dose'} logged at ${fmt24to12(actualTime)} (${label})`);

      // Show guardrail alerts if any
      if (result.guardrail_alerts?.length > 0) {
        setGuardrailAlerts(result.guardrail_alerts);
      }

      requestDynamicFeedback('completed', '', takeNowItem.name);
      setTakeNowItem(null);
      fetchFlow();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log dose');
    } finally {
      setTakeNowLoading(false);
    }
  };

  const handleOpenException = (item, mode) => {
    setExceptionItem(item);
    setExceptionMode(mode);
  };

  const handleSubmitException = async (formData) => {
    if (!exceptionItem) return;
    try {
      let res;
      if (formData.status === 'changed' && formData.actual_time) {
        // Use dedicated change-time endpoint for time modifications
        res = await axios.post(
          `${API}/v3/schedule/change-time/${today}/${exceptionItem.code || exceptionItem.item_id}`,
          {
            new_time: formData.actual_time,
            reason: formData.reason || 'user_adjusted',
          }
        );
      } else {
        // Use exception endpoint for skip/partial
        res = await axios.post(
          `${API}/v3/subscribers/${subscriberId}/daily-flow/${exceptionItem.item_id}/exception`,
          { execution_date: today, ...formData }
        );
      }
      if (res.data.warnings?.length > 0) {
        res.data.warnings.forEach(w => toast.warning(`${w.rule}: ${w.message}`));
      }
      const action = formData.status === 'skipped' ? 'skipped' : 'completed_late';
      toast.success(
        formData.status === 'skipped' ? 'Skipped' :
        formData.status === 'changed' ? `Time updated${res.data.variance_minutes != null ? ` (${res.data.variance_minutes}m variance)` : ''}` :
        'Change logged'
      );
      let blockKey = '';
      if (data?.blocks) {
        for (const block of data.blocks) {
          const allItems = block.groups.flatMap(g => g.items);
          if (allItems.find(i => i.item_id === exceptionItem.item_id)) { blockKey = block.block_key; break; }
        }
      }
      requestDynamicFeedback(action, blockKey, exceptionItem.name);
      setExceptionItem(null);
      fetchFlow();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || e.response?.data?.message || 'Failed to update');
      console.error('[CHANGE_TIME_ERROR]', e.response?.data || e.message);
    }
  };

  // Determine system state
  const systemState = 'stable'; // Could be derived from pattern direction

  if (loading || authLoading) {
    return (
      <BiologicalLayout systemState={systemState}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-[#8A8681] font-['Work_Sans']">Loading...</div>
        </div>
      </BiologicalLayout>
    );
  }

  if (!data?.has_flow) {
    return (
      <BiologicalLayout systemState={systemState}>
        <div className="max-w-2xl mx-auto px-6 sm:px-8 py-8" data-testid="daily-flow-experience">
          {nfActive && (
            <div data-testid="daily-tabs" className="flex justify-center gap-1 mb-6 border-b border-[#EBE8E3]">
              <button
                data-testid="daily-tab-daily"
                onClick={() => setActiveTab('daily')}
                className={`px-5 py-2 text-xs uppercase tracking-[0.15em] border-b-2 font-['Work_Sans'] transition-colors ${
                  activeTab === 'daily'
                    ? 'border-[#4A5D4E] text-[#2C2A29]'
                    : 'border-transparent text-[#8A8681] hover:text-[#2C2A29]'
                }`}
              >
                Daily Alignment
              </button>
              <button
                data-testid="daily-tab-neuroflow"
                onClick={() => setActiveTab('neuroflow')}
                className={`px-5 py-2 text-xs uppercase tracking-[0.15em] border-b-2 font-['Work_Sans'] transition-colors ${
                  activeTab === 'neuroflow'
                    ? 'border-[#4A5D4E] text-[#2C2A29]'
                    : 'border-transparent text-[#8A8681] hover:text-[#2C2A29]'
                }`}
              >
                NeuroFlow
              </button>
            </div>
          )}
          {activeTab === 'neuroflow' ? (
            <SubscriberNeuroFlowTab subscriberId={subscriberId} />
          ) : (
            <>
              <NeuroFlowDailyBanner overlay={nfOverlay} overlayError={nfOverlayError} />
              <div className="py-16 text-center">
                <Clock className="w-16 h-16 text-[#EBE8E3] mx-auto mb-6" strokeWidth={1} />
                <h2 className="text-2xl font-['Outfit'] font-light text-[#5C5955] mb-3">No Flow Today</h2>
                <p className="text-[#8A8681] font-['Work_Sans']">Your daily schedule will appear here once configured.</p>
              </div>
            </>
          )}
        </div>
      </BiologicalLayout>
    );
  }

  const { blocks, adherence, phase_name, date } = data;
  const currentBlockKey = getCurrentBlock(blocks);

  // Progress description (not gamified)
  const getProgressDescription = () => {
    const pct = adherence.percentage;
    if (pct === 100) return 'Day complete';
    if (pct >= 75) return 'Approaching end of day';
    if (pct >= 50) return 'Afternoon progressing';
    if (pct >= 25) return 'Morning progressing';
    return 'Day beginning';
  };

  return (
    <BiologicalLayout systemState={systemState}>
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-8" data-testid="daily-flow-experience">

        {/* ─── Phase D: Tab switcher (only when NeuroFlow active) ───────── */}
        {nfActive && (
          <div data-testid="daily-tabs" className="flex justify-center gap-1 mb-6 border-b border-[#EBE8E3]">
            <button
              data-testid="daily-tab-daily"
              onClick={() => setActiveTab('daily')}
              className={`px-5 py-2 text-xs uppercase tracking-[0.15em] border-b-2 font-['Work_Sans'] transition-colors ${
                activeTab === 'daily'
                  ? 'border-[#4A5D4E] text-[#2C2A29]'
                  : 'border-transparent text-[#8A8681] hover:text-[#2C2A29]'
              }`}
            >
              Daily Alignment
            </button>
            <button
              data-testid="daily-tab-neuroflow"
              onClick={() => setActiveTab('neuroflow')}
              className={`px-5 py-2 text-xs uppercase tracking-[0.15em] border-b-2 font-['Work_Sans'] transition-colors ${
                activeTab === 'neuroflow'
                  ? 'border-[#4A5D4E] text-[#2C2A29]'
                  : 'border-transparent text-[#8A8681] hover:text-[#2C2A29]'
              }`}
            >
              NeuroFlow
            </button>
          </div>
        )}

        {activeTab === 'neuroflow' ? (
          <SubscriberNeuroFlowTab subscriberId={subscriberId} />
        ) : (
        <>

        {/* ─── Phase E: NeuroFlow Daily Status Banner (read-only) ───────────── */}
        <NeuroFlowDailyBanner overlay={nfOverlay} overlayError={nfOverlayError} />

        {/* ─── HEADER: Current Position ─────────────────────────────────────── */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">
            {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-[#2C2A29] mb-1" data-testid="flow-title">
            Daily Alignment
          </h1>
          <p className="text-xs text-[#8C6B55] font-['Work_Sans'] uppercase tracking-[0.18em] mb-3">
            Today’s Alignment Pathway
          </p>
          <p className="text-sm text-[#5C5955] font-['Work_Sans'] max-w-md mx-auto leading-relaxed mb-2">
            These actions are not just tasks. They are daily inputs that help your system move in the right direction.
          </p>
          <p className="text-xs text-[#8A8681] font-['Work_Sans']" data-testid="flow-progress">
            {getProgressDescription()}
          </p>
        </div>

        {/* ─── SUBTLE PROGRESS (Not Gamified) ───────────────────────────────── */}
        <div className="flex items-center justify-center gap-6 mb-8 text-center">
          <div>
            <p className="text-3xl font-['Outfit'] font-light text-[#2C2A29]">
              {adherence.completed}<span className="text-xl text-[#8A8681]">/{adherence.total}</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mt-1">
              Completed
            </p>
          </div>
        </div>

        {/* ─── DYNAMIC FEEDBACK (Warm, Subtle Presence) ─────────────────────── */}
        {dynamicFeedback && (
          <div 
            className="text-center mb-16 transition-all duration-500 ease-in-out"
            data-testid="dynamic-feedback-message"
          >
            <p className="text-sm text-[#8C6B55] font-['Work_Sans'] font-light leading-relaxed">
              {dynamicFeedback.message}
            </p>
          </div>
        )}
        {!dynamicFeedback && <div className="mb-16" />}

        {/* ─── FLOW BLOCKS (Flowing Sections) ───────────────────────────────── */}
        <div className="space-y-16">
          {blocks.map((block, idx) => {
            const isCurrent = block.block_key === currentBlockKey;
            return (
              <div key={block.block_key} ref={isCurrent ? currentBlockRef : null}>
                <FlowBlock
                  block={block}
                  hint={hints[block.block_key]}
                  isCurrent={isCurrent}
                  onConfirm={handleConfirm}
                  onConfirmGroup={handleConfirmGroup}
                  onException={handleOpenException}
                  onTakeNow={handleOpenTakeNow}
                  confirming={confirming}
                  getOverlayFor={getOverlayFor}
                />
                {/* Flow separator (psychological transition) */}
                {idx < blocks.length - 1 && (
                  <div className="flex justify-center py-8">
                    <ChevronDown className="w-5 h-5 text-[#EBE8E3]" strokeWidth={1} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ─── DAILY REFLECTION PROMPT (routes to MindFlō) ──────────────────── */}
        <div className="mt-16">
          <DailyReflectionPrompt subscriberId={subscriberId} />
        </div>

        </>
        )}

      </div>

      {/* Exception Dialog */}
      <ExceptionDialog
        open={!!exceptionItem}
        onClose={() => setExceptionItem(null)}
        item={exceptionItem}
        onSubmit={handleSubmitException}
        mode={exceptionMode}
      />

      {/* Take Now Dialog */}
      <TakeNowDialog
        open={!!takeNowItem}
        onClose={() => setTakeNowItem(null)}
        onSubmit={handleSubmitTakeNow}
        loading={takeNowLoading}
      />

      {/* Guardrail Alerts (floating) */}
      {guardrailAlerts.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full px-4">
          <GuardrailAlert alerts={guardrailAlerts} onDismiss={() => setGuardrailAlerts([])} />
        </div>
      )}
    </BiologicalLayout>
  );
}
